import type { ReactNode } from 'react'
import { ApprovalRow } from '@/app/document-review/approval-row'
import type { OrderItem, TraderItem } from '@/app/document-review/approval-items'
import { ArrivalProvenance } from '@/app/document-review/email-provenance'
import { premiseText, premiseWords, type PremiseDraft } from '@/app/document-review/premise'
import { orderShape } from '@/app/document-review/proposal-shape'
import { OrderLink, TextWithLink } from '@/app/document-review/record-link'
import { TraderConfidence } from '@/app/document-review/trader-name'
import type { DocumentReviewDetailOutput } from '@/app/document-review/use-document-review'

export type PlanHeaderCardProps = {
  detail: DocumentReviewDetailOutput
  premise: PremiseDraft | null
  traderItem: TraderItem
  orderItem: OrderItem
  readOnly: boolean
  isConfirmed: (key: string) => boolean
  editingKey: string | null
  editorFor: (item: OrderItem | TraderItem) => ReactNode
  issueFor: (key: string) => string | null
  onToggle: (key: string) => void
  onEdit: (item: OrderItem | TraderItem) => void
}

function PremiseSentence({ detail, draft }: { detail: DocumentReviewDetailOutput; draft: PremiseDraft }) {
  return (
    <span aria-live="polite">
      {premiseWords(detail, draft).map((word, index) =>
        word.was === null ? (
          <span key={index}>{word.text}</span>
        ) : (
          <span key={index}>
            {word.was !== '' && <del className="text-muted-foreground mx-1 decoration-1">{word.was}</del>}
            {word.text !== '' && (
              <ins className="decoration-primary font-medium underline decoration-dotted decoration-2 underline-offset-4">
                {word.text}
              </ins>
            )}
          </span>
        )
      )}
    </span>
  )
}

function orderContent(detail: DocumentReviewDetailOutput, shown: string, whileDeciding: boolean): ReactNode {
  if (detail.order === null || detail.order.kind !== 'existing' || detail.orderDisplay === null) return undefined
  return (
    <TextWithLink
      text={shown}
      linked={detail.orderDisplay}
      link={<OrderLink orderIdDisplay={detail.orderDisplay} whileDeciding={whileDeciding} />}
    />
  )
}

export function PlanHeaderCard({
  detail,
  premise,
  traderItem,
  orderItem,
  readOnly,
  isConfirmed,
  editingKey,
  editorFor,
  issueFor,
  onToggle,
  onEdit
}: PlanHeaderCardProps) {
  const order = orderShape(detail)

  return (
    <section className="bg-card shrink-0 rounded-lg border">
      <header className="border-b px-3 py-2">
        <span className="text-muted-foreground text-xs">Files under</span>
      </header>
      <div className="divide-y py-1">
        <ApprovalRow
          approvalKey={traderItem.key}
          label="Processed as"
          shown={premiseText(detail)}
          shownContent={
            editingKey === traderItem.key && premise !== null ? (
              <PremiseSentence detail={detail} draft={premise} />
            ) : undefined
          }
          empty={detail.traderName === null}
          doubts={traderItem.doubts}
          confirmed={isConfirmed(traderItem.key)}
          needsApproval={traderItem.needsApproval}
          readOnly={readOnly}
          editable
          editing={editingKey === traderItem.key}
          editor={editingKey === traderItem.key ? editorFor(traderItem) : undefined}
          issue={issueFor(traderItem.key)}
          onToggle={() => onToggle(traderItem.key)}
          onEdit={() => onEdit(traderItem)}
          detail={
            <>
              {!traderItem.doubts.some((doubt) => doubt.kind === 'trader_uncertain') && (
                <span className="mt-0.5 block">
                  <TraderConfidence confidence={detail.traderConfidence} />
                </span>
              )}
              {detail.traderEvidence !== null && (
                <p className="text-muted-foreground mt-0.5 text-xs">{detail.traderEvidence}</p>
              )}
            </>
          }
        />
        <ApprovalRow
          approvalKey={orderItem.key}
          label="Order"
          shown={order.text}
          shownContent={orderContent(detail, order.text, !readOnly)}
          empty={detail.order === null}
          doubts={orderItem.doubts}
          confirmed={isConfirmed(orderItem.key)}
          needsApproval={orderItem.needsApproval}
          readOnly={readOnly}
          editable
          editing={editingKey === orderItem.key}
          editor={editingKey === orderItem.key ? editorFor(orderItem) : undefined}
          issue={issueFor(orderItem.key)}
          onToggle={() => onToggle(orderItem.key)}
          onEdit={() => onEdit(orderItem)}
          detail={
            order.detail === null ? undefined : <p className="text-muted-foreground mt-0.5 text-xs">{order.detail}</p>
          }
        />
      </div>
      <ArrivalProvenance arrival={detail} />
    </section>
  )
}
